import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "leftInput",
    "rightInput",
    "leftLines",
    "rightLines",
    "leftCounter",
    "rightCounter",
    "ignoreWhitespaceInput",
    "ignoreCaseInput",
    "viewModeInput",
    "compareButton",
    "error",
    "result",
    "saveLeftInput",
    "saveRightInput",
    "saveIgnoreWhitespaceInput",
    "saveIgnoreCaseInput",
    "saveViewModeInput"
  ]

  static values = {
    maxCharsPerField: Number,
    maxCombinedChars: Number,
    tooLongMessage: String,
    tooLongCombinedMessage: String,
    unchangedMessage: String,
    emptyResultMessage: String,
    addedLabel: String,
    removedLabel: String,
    sampleLeft: String,
    sampleRight: String
  }

  connect() {
    this.inputsChanged()
    this.syncSaveFields()
  }

  inputsChanged() {
    this.updateCounters()
    this.updateLineNumbers()
    this.syncSaveFields()
    this.validateLimits()
  }

  compare() {
    if (!this.validateLimits()) return

    const leftLines = this.leftInputTarget.value.split("\n")
    const rightLines = this.rightInputTarget.value.split("\n")

    const ops = this.diffLines(leftLines, rightLines)
    this.render(ops)
    this.syncSaveFields()
  }

  clearLeft() {
    this.leftInputTarget.value = ""
    this.inputsChanged()
  }

  clearRight() {
    this.rightInputTarget.value = ""
    this.inputsChanged()
  }

  clearAll() {
    this.leftInputTarget.value = ""
    this.rightInputTarget.value = ""
    this.resultTarget.innerHTML = `<p>${this.emptyResultMessageValue}</p>`
    this.inputsChanged()
  }

  swap() {
    const left = this.leftInputTarget.value
    this.leftInputTarget.value = this.rightInputTarget.value
    this.rightInputTarget.value = left
    this.inputsChanged()
  }

  loadSample() {
    this.leftInputTarget.value = this.sampleLeftValue
    this.rightInputTarget.value = this.sampleRightValue
    this.inputsChanged()
  }

  updateCounters() {
    this.leftCounterTarget.textContent = `${this.leftInputTarget.value.length} / ${this.maxCharsPerFieldValue}`
    this.rightCounterTarget.textContent = `${this.rightInputTarget.value.length} / ${this.maxCharsPerFieldValue}`
  }

  updateLineNumbers() {
    this.leftLinesTarget.textContent = this.buildLineNumbers(this.leftInputTarget.value)
    this.rightLinesTarget.textContent = this.buildLineNumbers(this.rightInputTarget.value)
    this.syncLeftScroll()
    this.syncRightScroll()
  }

  buildLineNumbers(value) {
    const lineCount = Math.max(1, value.split("\n").length)
    return Array.from({ length: lineCount }, (_, index) => String(index + 1)).join("\n")
  }

  syncLeftScroll() {
    this.leftLinesTarget.scrollTop = this.leftInputTarget.scrollTop
  }

  syncRightScroll() {
    this.rightLinesTarget.scrollTop = this.rightInputTarget.scrollTop
  }

  validateLimits() {
    const leftLength = this.leftInputTarget.value.length
    const rightLength = this.rightInputTarget.value.length
    const combinedLength = leftLength + rightLength

    let error = ""
    if (leftLength > this.maxCharsPerFieldValue || rightLength > this.maxCharsPerFieldValue) {
      error = this.tooLongMessageValue
    } else if (combinedLength > this.maxCombinedCharsValue) {
      error = this.tooLongCombinedMessageValue
    }

    this.errorTarget.textContent = error
    this.errorTarget.classList.toggle("hidden", error.length === 0)
    this.compareButtonTarget.disabled = error.length > 0

    return error.length === 0
  }

  normalizeLine(line) {
    let value = line

    if (this.ignoreWhitespaceInputTarget.checked) {
      value = value.replace(/\s+/g, " ").trim()
    }

    if (this.ignoreCaseInputTarget.checked) {
      value = value.toLowerCase()
    }

    return value
  }

  diffLines(leftLines, rightLines) {
    const leftNorm = leftLines.map((line) => this.normalizeLine(line))
    const rightNorm = rightLines.map((line) => this.normalizeLine(line))

    const rows = leftNorm.length + 1
    const cols = rightNorm.length + 1
    const lcs = Array.from({ length: rows }, () => Array(cols).fill(0))

    for (let i = 1; i < rows; i += 1) {
      for (let j = 1; j < cols; j += 1) {
        if (leftNorm[i - 1] === rightNorm[j - 1]) {
          lcs[i][j] = lcs[i - 1][j - 1] + 1
        } else {
          lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1])
        }
      }
    }

    const result = []
    let i = leftNorm.length
    let j = rightNorm.length

    while (i > 0 || j > 0) {
      if (i > 0 && j > 0 && leftNorm[i - 1] === rightNorm[j - 1]) {
        result.push({ type: "equal", left: leftLines[i - 1], right: rightLines[j - 1] })
        i -= 1
        j -= 1
      } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
        result.push({ type: "add", left: "", right: rightLines[j - 1] })
        j -= 1
      } else if (i > 0) {
        result.push({ type: "remove", left: leftLines[i - 1], right: "" })
        i -= 1
      }
    }

    return result.reverse()
  }

  render(ops) {
    if (ops.every((op) => op.type === "equal")) {
      this.resultTarget.innerHTML = `<p>${this.unchangedMessageValue}</p>`
      return
    }

    const mode = this.selectedViewMode()

    if (mode === "inline") {
      this.renderInline(ops)
      return
    }

    this.renderSideBySide(ops)
  }

  renderInline(ops) {
    const wrapper = document.createElement("div")
    wrapper.className = "space-y-1"
    let leftLine = 1
    let rightLine = 1

    ops.forEach((op) => {
      const row = document.createElement("div")
      row.className = `grid grid-cols-[3rem_3rem_1fr] items-start gap-3 rounded border px-2 py-1 font-mono text-xs ${this.inlineRowClass(op.type)}`

      const prefix = op.type === "add" ? "+" : op.type === "remove" ? "-" : " "
      const content = op.type === "add" ? op.right : op.left

      const leftNumber = document.createElement("span")
      leftNumber.className = "select-none text-slate-500 dark:text-slate-400"
      leftNumber.textContent = op.type === "add" ? "" : String(leftLine)

      const rightNumber = document.createElement("span")
      rightNumber.className = "select-none text-slate-500 dark:text-slate-400"
      rightNumber.textContent = op.type === "remove" ? "" : String(rightLine)

      const text = document.createElement("span")
      text.className = "break-words whitespace-pre-wrap"
      text.textContent = `${prefix} ${content}`

      row.appendChild(leftNumber)
      row.appendChild(rightNumber)
      row.appendChild(text)
      wrapper.appendChild(row)

      if (op.type !== "add") leftLine += 1
      if (op.type !== "remove") rightLine += 1
    })

    this.resultTarget.innerHTML = ""
    this.resultTarget.appendChild(wrapper)
  }

  renderSideBySide(ops) {
    const table = document.createElement("table")
    table.className = "min-w-full table-fixed border-collapse text-xs"
    let leftLine = 1
    let rightLine = 1

    const header = document.createElement("thead")
    header.innerHTML = `
      <tr class="text-left text-slate-600 dark:text-slate-300">
        <th class="w-1/2 border-b border-slate-300 px-2 py-1">${this.removedLabelValue}</th>
        <th class="w-1/2 border-b border-slate-300 px-2 py-1">${this.addedLabelValue}</th>
      </tr>
    `
    table.appendChild(header)

    const body = document.createElement("tbody")

    ops.forEach((op) => {
      const row = document.createElement("tr")

      const leftCell = document.createElement("td")
      leftCell.className = `align-top border-b border-slate-200 px-2 py-1 font-mono text-xs ${this.sideCellClass(op.type, "left")}`

      const rightCell = document.createElement("td")
      rightCell.className = `align-top border-b border-slate-200 px-2 py-1 font-mono text-xs ${this.sideCellClass(op.type, "right")}`

      leftCell.appendChild(this.buildSideCellContent(op.type === "add" ? "" : op.left, op.type === "add" ? null : leftLine))
      rightCell.appendChild(this.buildSideCellContent(op.type === "remove" ? "" : op.right, op.type === "remove" ? null : rightLine))

      row.appendChild(leftCell)
      row.appendChild(rightCell)
      body.appendChild(row)

      if (op.type !== "add") leftLine += 1
      if (op.type !== "remove") rightLine += 1
    })

    table.appendChild(body)
    this.resultTarget.innerHTML = ""
    this.resultTarget.appendChild(table)
  }

  buildSideCellContent(content, lineNumber) {
    const wrapper = document.createElement("div")
    wrapper.className = "grid grid-cols-[2.5rem_1fr] gap-2"

    const number = document.createElement("span")
    number.className = "select-none text-slate-500 dark:text-slate-400"
    number.textContent = lineNumber ? String(lineNumber) : ""

    const text = document.createElement("span")
    text.className = "break-words whitespace-pre-wrap"
    text.textContent = content

    wrapper.appendChild(number)
    wrapper.appendChild(text)
    return wrapper
  }

  inlineRowClass(type) {
    if (type === "add") return "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-200"
    if (type === "remove") return "border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900/40 dark:bg-rose-900/20 dark:text-rose-200"

    return "border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
  }

  sideCellClass(type, side) {
    if (type === "equal") return "bg-slate-50 text-slate-700 dark:bg-slate-900 dark:text-slate-300"
    if (type === "remove" && side === "left") return "bg-rose-50 text-rose-900 dark:bg-rose-900/20 dark:text-rose-200"
    if (type === "add" && side === "right") return "bg-emerald-50 text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-200"

    return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500"
  }

  selectedViewMode() {
    const selected = this.viewModeInputTargets.find((input) => input.checked)
    return selected?.value || "side_by_side"
  }

  syncSaveFields() {
    if (!this.hasSaveLeftInputTarget) return

    this.saveLeftInputTarget.value = this.leftInputTarget.value
    this.saveRightInputTarget.value = this.rightInputTarget.value
    this.saveIgnoreWhitespaceInputTarget.value = this.ignoreWhitespaceInputTarget.checked
    this.saveIgnoreCaseInputTarget.value = this.ignoreCaseInputTarget.checked
    this.saveViewModeInputTarget.value = this.selectedViewMode()
  }
}
